import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import styled from "styled-components/macro";
import { Box, Button, Typography } from "@material-ui/core";

const SocialButtonWrap = styled(Box)`
  margin-top: 18px;
  width: 100%;
`;

const DividerRow = styled(Box)`
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 18px 0;
  color: #94a3b8;
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;

  &::before,
  &::after {
    content: "";
    flex: 1;
    height: 1px;
    background: rgba(148, 163, 184, 0.24);
  }
`;

const MissingConfigButton = styled(Button)`
  min-height: 46px;
  border-radius: 16px;
  text-transform: none;
  font-weight: 700;
`;

const MissingConfigText = styled(Typography)`
  margin-top: 8px;
  color: #94a3b8;
  font-size: 0.8rem;
`;

const GoogleButtonFrame = styled(Box)`
  width: 100%;
  border-radius: 18px;
  overflow: hidden;
`;

const GoogleButtonHost = styled(Box)`
  width: 100%;
  display: flex;
  align-items: stretch;
  justify-content: stretch;

  & > div,
  & > div > div,
  & iframe {
    width: 100% !important;
    min-width: 100% !important;
    max-width: 100% !important;
  }
`;

function GoogleSignInButton({
  onCredential,
  text = "signin_with",
  title = "O continúe con Google",
}) {
  const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
  const frameRef = useRef(null);
  const hostRef = useRef(null);
  const resizeFrameRef = useRef(null);
  const [buttonWidth, setButtonWidth] = useState(0);
  const [isReady, setIsReady] = useState(Boolean(window.google?.accounts?.id));

  const buttonConfig = useMemo(
    () => ({
      theme: "outline",
      size: "large",
      type: "standard",
      text,
      shape: "pill",
      logo_alignment: "left",
      width: String(buttonWidth || 320),
    }),
    [buttonWidth, text]
  );

  useEffect(() => {
    if (!frameRef.current) {
      return undefined;
    }

    const updateWidth = () => {
      const nextWidth = Math.round(
        frameRef.current.getBoundingClientRect().width
      );

      if (nextWidth > 0) {
        setButtonWidth((currentWidth) =>
          currentWidth === nextWidth ? currentWidth : nextWidth
        );
      }
    };

    const scheduleWidthUpdate = () => {
      if (resizeFrameRef.current) {
        cancelAnimationFrame(resizeFrameRef.current);
      }

      resizeFrameRef.current = window.requestAnimationFrame(() => {
        resizeFrameRef.current = null;
        updateWidth();
      });
    };

    updateWidth();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", scheduleWidthUpdate);

      return () => {
        if (resizeFrameRef.current) {
          cancelAnimationFrame(resizeFrameRef.current);
        }
        window.removeEventListener("resize", scheduleWidthUpdate);
      };
    }

    const resizeObserver = new ResizeObserver(scheduleWidthUpdate);
    resizeObserver.observe(frameRef.current);

    return () => {
      if (resizeFrameRef.current) {
        cancelAnimationFrame(resizeFrameRef.current);
      }
      resizeObserver.disconnect();
    };
  }, []);

  useEffect(() => {
    if (!clientId || window.google?.accounts?.id) {
      setIsReady(Boolean(window.google?.accounts?.id));
      return undefined;
    }

    const existingScript = document.querySelector(
      'script[src="https://accounts.google.com/gsi/client"]'
    );

    if (existingScript) {
      existingScript.addEventListener("load", () => setIsReady(true));
      return undefined;
    }

    const script = document.createElement("script");
    script.src = "https://accounts.google.com/gsi/client";
    script.async = true;
    script.defer = true;
    script.onload = () => setIsReady(true);
    document.body.appendChild(script);

    return () => {
      script.onload = null;
    };
  }, [clientId]);

  useEffect(() => {
    if (
      !clientId ||
      !isReady ||
      !hostRef.current ||
      !window.google?.accounts?.id
    ) {
      return;
    }

    hostRef.current.innerHTML = "";
    window.google.accounts.id.initialize({
      client_id: clientId,
      callback: ({ credential }) => onCredential(credential),
      ux_mode: "popup",
    });

    window.google.accounts.id.renderButton(hostRef.current, buttonConfig);
  }, [buttonConfig, clientId, isReady, onCredential]);

  return (
    <SocialButtonWrap>
      <DividerRow>{title}</DividerRow>
      {clientId ? (
        <GoogleButtonFrame ref={frameRef}>
          <GoogleButtonHost ref={hostRef} />
        </GoogleButtonFrame>
      ) : (
        <>
          <MissingConfigButton fullWidth variant="outlined" disabled>
            Google no configurado
          </MissingConfigButton>
          <MissingConfigText>
            Defina REACT_APP_GOOGLE_CLIENT_ID para habilitar este acceso.
          </MissingConfigText>
        </>
      )}
    </SocialButtonWrap>
  );
}

GoogleSignInButton.propTypes = {
  onCredential: PropTypes.func.isRequired,
  text: PropTypes.string,
  title: PropTypes.string,
};

export default GoogleSignInButton;
